import * as React from 'react'
import {
  Dices,
  Loader2,
  CheckCircle2,
  ChevronDown,
  Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  generateUniformBits,
  generateBernoulliBits,
  generateGaussianSamples,
  generateMarkovChain,
  generateDriftingBias,
  generateUniformSamples,
  countOnes,
  type GeneratedData,
} from '@/lib/randomDataGenerator'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DataGeneratorMeta {
  bitLength?: number
  sampleCount: number
  description: string
  parameters: Record<string, number | string>
}

export interface DataGeneratorProps {
  mode: 'binary' | 'continuous'
  onDataGenerated: (
    data: Uint8Array | Float64Array,
    meta: DataGeneratorMeta,
  ) => void
}

// ---------------------------------------------------------------------------
// Binary distribution types
// ---------------------------------------------------------------------------

type BinaryDistribution = 'uniform' | 'bernoulli' | 'markov' | 'drifting'
type ContinuousDistribution = 'standard-normal' | 'custom-gaussian' | 'uniform'

const BINARY_BIT_PRESETS = [
  { label: '10K', value: 10_000 },
  { label: '100K', value: 100_000 },
  { label: '500K', value: 500_000 },
  { label: '1M', value: 1_000_000 },
]

const CONTINUOUS_SAMPLE_PRESETS = [
  { label: '1K', value: 1_000 },
  { label: '10K', value: 10_000 },
  { label: '100K', value: 100_000 },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

function formatDuration(ms: number): string {
  if (ms < 1) return '<1ms'
  if (ms < 1000) return `${ms.toFixed(0)}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataGenerator({ mode, onDataGenerated }: DataGeneratorProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isGenerating, setIsGenerating] = React.useState(false)

  // Binary mode state
  const [numBits, setNumBits] = React.useState(1_000_000)
  const [binaryDist, setBinaryDist] = React.useState<BinaryDistribution>('uniform')
  const [bernoulliP, setBernoulliP] = React.useState('0.5')
  const [markovP01, setMarkovP01] = React.useState('0.5')
  const [markovP10, setMarkovP10] = React.useState('0.5')
  const [driftStart, setDriftStart] = React.useState('0.3')
  const [driftEnd, setDriftEnd] = React.useState('0.7')

  // Continuous mode state
  const [numSamples, setNumSamples] = React.useState(10_000)
  const [continuousDist, setContinuousDist] = React.useState<ContinuousDistribution>('standard-normal')
  const [gaussianMu, setGaussianMu] = React.useState('0')
  const [gaussianSigma, setGaussianSigma] = React.useState('1')

  // Result state
  const [generationResult, setGenerationResult] = React.useState<{
    sampleCount: number
    proportionOfOnes?: number
    description: string
    durationMs: number
    parameters: Record<string, number | string>
  } | null>(null)

  // ---------------------------------------------------------------------------
  // Generate handler
  // ---------------------------------------------------------------------------
  const handleGenerate = React.useCallback(() => {
    setIsGenerating(true)
    setGenerationResult(null)

    // Use requestAnimationFrame to allow spinner to render
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          const startTime = performance.now()
          let result: GeneratedData

          if (mode === 'binary') {
            switch (binaryDist) {
              case 'uniform':
                result = generateUniformBits(numBits)
                break
              case 'bernoulli': {
                const p = parseFloat(bernoulliP) || 0.5
                result = generateBernoulliBits(numBits, Math.max(0.01, Math.min(0.99, p)))
                break
              }
              case 'markov': {
                const p01 = parseFloat(markovP01) || 0.5
                const p10 = parseFloat(markovP10) || 0.5
                result = generateMarkovChain(
                  numBits,
                  Math.max(0.01, Math.min(0.99, p01)),
                  Math.max(0.01, Math.min(0.99, p10)),
                )
                break
              }
              case 'drifting': {
                const pStart = parseFloat(driftStart) || 0.3
                const pEnd = parseFloat(driftEnd) || 0.7
                result = generateDriftingBias(
                  numBits,
                  Math.max(0.01, Math.min(0.99, pStart)),
                  Math.max(0.01, Math.min(0.99, pEnd)),
                )
                break
              }
            }
          } else {
            switch (continuousDist) {
              case 'standard-normal':
                result = generateGaussianSamples(numSamples, 0, 1)
                break
              case 'custom-gaussian': {
                const mu = parseFloat(gaussianMu) || 0
                const sigma = parseFloat(gaussianSigma) || 1
                result = generateGaussianSamples(numSamples, mu, Math.max(0.001, sigma))
                break
              }
              case 'uniform':
                result = generateUniformSamples(numSamples)
                break
            }
          }

          const durationMs = performance.now() - startTime

          // Compute proportion of ones for binary data
          let proportionOfOnes: number | undefined
          if (result.data instanceof Uint8Array && result.bitLength) {
            const ones = countOnes(result.data, result.bitLength)
            proportionOfOnes = ones / result.bitLength
          }

          setGenerationResult({
            sampleCount: result.sampleCount,
            proportionOfOnes,
            description: result.description,
            durationMs,
            parameters: result.parameters,
          })

          onDataGenerated(result.data, {
            bitLength: result.bitLength,
            sampleCount: result.sampleCount,
            description: result.description,
            parameters: result.parameters,
          })
        } catch (err) {
          console.error('Data generation failed:', err)
        } finally {
          setIsGenerating(false)
        }
      }, 16) // small delay to let UI update
    })
  }, [
    mode,
    numBits,
    binaryDist,
    bernoulliP,
    markovP01,
    markovP10,
    driftStart,
    driftEnd,
    numSamples,
    continuousDist,
    gaussianMu,
    gaussianSigma,
    onDataGenerated,
  ])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Card className="border-dashed">
      {/* Collapsible header */}
      <CardHeader
        className={cn('cursor-pointer select-none', isOpen ? 'pb-0' : 'pb-5')}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Dices className="h-4 w-4 text-primary" />
            Generate Test Data
          </CardTitle>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </CardHeader>

      {isOpen && (
        <CardContent className="pt-4 space-y-4">
          {mode === 'binary' ? (
            <BinaryControls
              numBits={numBits}
              setNumBits={setNumBits}
              distribution={binaryDist}
              setDistribution={setBinaryDist}
              bernoulliP={bernoulliP}
              setBernoulliP={setBernoulliP}
              markovP01={markovP01}
              setMarkovP01={setMarkovP01}
              markovP10={markovP10}
              setMarkovP10={setMarkovP10}
              driftStart={driftStart}
              setDriftStart={setDriftStart}
              driftEnd={driftEnd}
              setDriftEnd={setDriftEnd}
              disabled={isGenerating}
            />
          ) : (
            <ContinuousControls
              numSamples={numSamples}
              setNumSamples={setNumSamples}
              distribution={continuousDist}
              setDistribution={setContinuousDist}
              gaussianMu={gaussianMu}
              setGaussianMu={setGaussianMu}
              gaussianSigma={gaussianSigma}
              setGaussianSigma={setGaussianSigma}
              disabled={isGenerating}
            />
          )}

          <Separator />

          {/* Generate button */}
          <Button
            className="w-full"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Dices className="h-4 w-4" />
                Generate
              </>
            )}
          </Button>

          {/* Success banner */}
          {generationResult && !isGenerating && (
            <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">
                  Data generated successfully
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Samples:</span>
                <span className="font-mono text-foreground">
                  {formatNumber(generationResult.sampleCount)}
                </span>
                {generationResult.proportionOfOnes !== undefined && (
                  <>
                    <span>P(1) observed:</span>
                    <span className="font-mono text-foreground">
                      {generationResult.proportionOfOnes.toFixed(6)}
                    </span>
                  </>
                )}
                <span>Time:</span>
                <span className="font-mono text-foreground">
                  {formatDuration(generationResult.durationMs)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Binary Controls Sub-component
// ---------------------------------------------------------------------------

interface BinaryControlsProps {
  numBits: number
  setNumBits: (v: number) => void
  distribution: BinaryDistribution
  setDistribution: (v: BinaryDistribution) => void
  bernoulliP: string
  setBernoulliP: (v: string) => void
  markovP01: string
  setMarkovP01: (v: string) => void
  markovP10: string
  setMarkovP10: (v: string) => void
  driftStart: string
  setDriftStart: (v: string) => void
  driftEnd: string
  setDriftEnd: (v: string) => void
  disabled: boolean
}

function BinaryControls({
  numBits,
  setNumBits,
  distribution,
  setDistribution,
  bernoulliP,
  setBernoulliP,
  markovP01,
  setMarkovP01,
  markovP10,
  setMarkovP10,
  driftStart,
  setDriftStart,
  driftEnd,
  setDriftEnd,
  disabled,
}: BinaryControlsProps) {
  const [customBits, setCustomBits] = React.useState('')
  const isCustom = !BINARY_BIT_PRESETS.some((p) => p.value === numBits)

  const handleCustomChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      setCustomBits(raw)
      const n = parseInt(raw, 10)
      if (!isNaN(n) && n >= 100) {
        setNumBits(Math.min(n, 10_000_000))
      }
    },
    [setNumBits],
  )

  const handlePresetClick = React.useCallback(
    (value: number) => {
      setCustomBits('')
      setNumBits(value)
    },
    [setNumBits],
  )

  return (
    <div className="space-y-4">
      {/* Number of bits */}
      <div className="space-y-2">
        <Label className="text-xs">Number of bits</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {BINARY_BIT_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={!isCustom && numBits === preset.value ? 'default' : 'outline'}
              size="sm"
              className="flex-1 min-w-[3rem] text-xs h-8"
              onClick={() => handlePresetClick(preset.value)}
              disabled={disabled}
            >
              {preset.label}
            </Button>
          ))}
          <span className="text-xs text-muted-foreground shrink-0">or</span>
          <Input
            type="number"
            min={100}
            max={10000000}
            placeholder="e.g., 50000"
            value={customBits}
            onChange={handleCustomChange}
            disabled={disabled}
            className="h-8 w-24 text-xs font-mono"
          />
        </div>
      </div>

      {/* Distribution type */}
      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1.5">
          Distribution
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground/50 cursor-help">
                  <Info className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[240px]">
                <p>Distribution used to generate synthetic test data. Uniform: unbiased P(1) = 0.5 using Web Crypto API. Bernoulli(p): biased with custom probability. Markov: first-order chain with transition dependencies. Drifting: linearly changing bias over time.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <Select
          value={distribution}
          onValueChange={(v) => setDistribution(v as BinaryDistribution)}
          disabled={disabled}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="uniform">Uniform (p = 0.5)</SelectItem>
            <SelectItem value="bernoulli">Bernoulli (custom bias)</SelectItem>
            <SelectItem value="markov">Markov chain</SelectItem>
            <SelectItem value="drifting">Drifting bias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Distribution-specific parameters */}
      {distribution === 'bernoulli' && (
        <div className="space-y-2 rounded-md bg-muted/50 p-3">
          <div className="space-y-1.5">
            <Label htmlFor="bernoulli-p" className="text-xs flex items-center gap-1.5">
              Bias p = P(bit = 1)
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Probability that each generated bit equals 1. Range 0.01–0.99. At p = 0.5 this is equivalent to uniform. Higher values produce more 1s.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="bernoulli-p"
              type="number"
              step="0.01"
              min="0.01"
              max="0.99"
              value={bernoulliP}
              onChange={(e) => setBernoulliP(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
            <p className="text-[10px] text-muted-foreground">
              Range: 0.01 to 0.99
            </p>
          </div>
        </div>
      )}

      {distribution === 'markov' && (
        <div className="space-y-3 rounded-md bg-muted/50 p-3">
          <div className="space-y-1.5">
            <Label htmlFor="markov-p01" className="text-xs flex items-center gap-1.5">
              p{'\u2080\u2081'} = P(0 {'->'} 1)
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Transition probability P(X{'\u209C'} = 1 | X{'\u209C\u208B\u2081'} = 0). Probability of switching from 0 to 1. Along with p{'\u2081\u2080'}, determines the stationary distribution and autocorrelation strength.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="markov-p01"
              type="number"
              step="0.01"
              min="0.01"
              max="0.99"
              value={markovP01}
              onChange={(e) => setMarkovP01(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="markov-p10" className="text-xs flex items-center gap-1.5">
              p{'\u2081\u2080'} = P(1 {'->'} 0)
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Transition probability P(X{'\u209C'} = 0 | X{'\u209C\u208B\u2081'} = 1). Probability of switching from 1 to 0. When p{'\u2080\u2081'} + p{'\u2081\u2080'} {'<'} 1, the chain tends to stay in the same state (positive autocorrelation).</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="markov-p10"
              type="number"
              step="0.01"
              min="0.01"
              max="0.99"
              value={markovP10}
              onChange={(e) => setMarkovP10(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Stationary P(1) = p{'\u2080\u2081'} / (p{'\u2080\u2081'} + p{'\u2081\u2080'})
          </p>
        </div>
      )}

      {distribution === 'drifting' && (
        <div className="space-y-3 rounded-md bg-muted/50 p-3">
          <div className="space-y-1.5">
            <Label htmlFor="drift-start" className="text-xs flex items-center gap-1.5">
              Start bias (pStart)
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Initial bias at the beginning of the sequence. The bias changes linearly from pStart to pEnd across the full sequence length.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="drift-start"
              type="number"
              step="0.01"
              min="0.01"
              max="0.99"
              value={driftStart}
              onChange={(e) => setDriftStart(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="drift-end" className="text-xs flex items-center gap-1.5">
              End bias (pEnd)
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Final bias at the end of the sequence. If pEnd {'\u2260'} pStart, the stability test should detect the drift.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="drift-end"
              type="number"
              step="0.01"
              min="0.01"
              max="0.99"
              value={driftEnd}
              onChange={(e) => setDriftEnd(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Bias drifts linearly from pStart to pEnd over the sequence
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Continuous Controls Sub-component
// ---------------------------------------------------------------------------

interface ContinuousControlsProps {
  numSamples: number
  setNumSamples: (v: number) => void
  distribution: ContinuousDistribution
  setDistribution: (v: ContinuousDistribution) => void
  gaussianMu: string
  setGaussianMu: (v: string) => void
  gaussianSigma: string
  setGaussianSigma: (v: string) => void
  disabled: boolean
}

function ContinuousControls({
  numSamples,
  setNumSamples,
  distribution,
  setDistribution,
  gaussianMu,
  setGaussianMu,
  gaussianSigma,
  setGaussianSigma,
  disabled,
}: ContinuousControlsProps) {
  const [customSamples, setCustomSamples] = React.useState('')
  const isCustom = !CONTINUOUS_SAMPLE_PRESETS.some((p) => p.value === numSamples)

  const handleCustomChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      setCustomSamples(raw)
      const n = parseInt(raw, 10)
      if (!isNaN(n) && n >= 20) {
        setNumSamples(Math.min(n, 1_000_000))
      }
    },
    [setNumSamples],
  )

  const handlePresetClick = React.useCallback(
    (value: number) => {
      setCustomSamples('')
      setNumSamples(value)
    },
    [setNumSamples],
  )

  return (
    <div className="space-y-4">
      {/* Number of samples */}
      <div className="space-y-2">
        <Label className="text-xs">Number of samples</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {CONTINUOUS_SAMPLE_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={!isCustom && numSamples === preset.value ? 'default' : 'outline'}
              size="sm"
              className="flex-1 min-w-[3rem] text-xs h-8"
              onClick={() => handlePresetClick(preset.value)}
              disabled={disabled}
            >
              {preset.label}
            </Button>
          ))}
          <span className="text-xs text-muted-foreground shrink-0">or</span>
          <Input
            type="number"
            min={20}
            max={10000000}
            placeholder="e.g., 5000"
            value={customSamples}
            onChange={handleCustomChange}
            disabled={disabled}
            className="h-8 w-24 text-xs font-mono"
          />
        </div>
      </div>

      {/* Distribution type */}
      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1.5">
          Distribution
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground/50 cursor-help">
                  <Info className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[240px]">
                <p>Distribution for generated continuous samples. Standard Normal: {'\u03BC'}=0, {'\u03C3'}=1. Custom Gaussian: specify mean and standard deviation. Uniform: U[0,1), useful for testing normality rejection.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <Select
          value={distribution}
          onValueChange={(v) => setDistribution(v as ContinuousDistribution)}
          disabled={disabled}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="standard-normal">
              Standard Normal ({'\u03BC'}=0, {'\u03C3'}=1)
            </SelectItem>
            <SelectItem value="custom-gaussian">
              Custom Gaussian
            </SelectItem>
            <SelectItem value="uniform">
              Uniform [0, 1) (non-Gaussian)
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Custom Gaussian parameters */}
      {distribution === 'custom-gaussian' && (
        <div className="space-y-3 rounded-md bg-muted/50 p-3">
          <div className="space-y-1.5">
            <Label htmlFor="gaussian-mu" className="text-xs flex items-center gap-1.5">
              Mean ({'\u03BC'})
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Mean of the Gaussian distribution to generate. The generated samples will have sample mean approximately equal to this value.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="gaussian-mu"
              type="number"
              step="any"
              value={gaussianMu}
              onChange={(e) => setGaussianMu(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gaussian-sigma" className="text-xs flex items-center gap-1.5">
              Std Dev ({'\u03C3'})
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px]">
                    <p>Standard deviation of the Gaussian distribution to generate. Must be positive. Controls the spread of generated values.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="gaussian-sigma"
              type="number"
              step="any"
              min="0.001"
              value={gaussianSigma}
              onChange={(e) => setGaussianSigma(e.target.value)}
              disabled={disabled}
              className="h-8 font-mono text-sm"
            />
          </div>
        </div>
      )}
    </div>
  )
}
